import { useStore } from '@/src/models/Root'
import {
  ContentNotFound,
  ExhibitionAndEventCollections,
  Hero,
  Layout,
} from '@common'
import {
  IExhibitionAndEventScreen,
  IPageMetaDataExhibitionAndEventPage,
} from '@interfaces'
import { eMUSEUM_PAGINATION_LIMIT } from '@lib/const'
import {
  EventService,
  ExhibitionService,
  OnlineExhibitionsService,
  PageService,
  ToursService,
} from '@services'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

const ExhibitionAndEventScreen = ({
  pageData,
  metaData,
  global: { navigation },
}: IExhibitionAndEventScreen) => {
  const { t } = useTranslation()
  const { asPath } = useRouter()
  const {
    exhibitionAndEvents: { setMetaData },
  } = useStore()

  useEffect(() => {
    if (!pageData.error) {
      setMetaData(metaData)
    }
  }, [metaData, setMetaData, pageData])
  const { query } = useRouter()

  const getTitleAndSubtitle = (eventType: string) => {
    const mapping = {
      'Learning-Kid-Workshop': {
        title: t('exhibition-and-event:learningKidWorkshop.title'),
        subTitle: t('exhibition-and-event:learningKidWorkshop.subTitle'),
      },
      'Learning-Adult-Workshop': {
        title: t('exhibition-and-event:learningAdultWorkshop.title'),
        subTitle: t('exhibition-and-event:learningAdultWorkshop.subTitle'),
      },
      'Learning-Adult-Seminar': {
        title: t('exhibition-and-event:learningAdultSeminar.title'),
        subTitle: t('exhibition-and-event:learningAdultSeminar.subTitle'),
      },
      'Learning-Conference': {
        title: t('exhibition-and-event:learningConference.title'),
        subTitle: t('exhibition-and-event:learningConference.subTitle'),
      }
    }
    if (eventType && mapping[eventType]) {
      return mapping[eventType]
    }

    return {
      title: t('exhibition-and-event:exhibitions.title'),
      subTitle: t('exhibition-and-event:exhibitions.subTitle'),
    }
  }

  const dynamicHeroText = getTitleAndSubtitle(query.eventType as string)

  if (pageData.error) {
    return (
      <Layout
        title={t('exhibition-and-event:home.title')}
        navigation={navigation}
        path={asPath}
      >
        <ContentNotFound infoType="serverSideError" />
      </Layout>
    )
  }
  return (
    <Layout
      title={t('exhibition-and-event:home.title')}
      navigation={navigation}
      path={asPath}
    >
      <Hero
        bgImageUrl={
          pageData?.data?.hero?.bgImageUrl ||
          '/img/exhibition-and-event/banner.jpeg'
        }
        subTitle={dynamicHeroText.subTitle}
        title={dynamicHeroText.title}
        description={pageData?.data?.hero?.description}
      />
      <ExhibitionAndEventCollections
        collectionData={pageData?.data?.collection}
        metaData={metaData}
        isWithExpandedExhibitionData={false}
      />
    </Layout>
  )
}

export default ExhibitionAndEventScreen

interface ISSR {
  locale: string
  query: IPageMetaDataExhibitionAndEventPage
}

export async function getServerSideProps({ locale, query }: ISSR) {
  const page = query?.page || '1'
  const totalPage = '1'
  const activeCollection = query?.activeCollection || 'exhibitions'
  const sort = query?.sort || 'current'
  const totalCount = 0
  const limit = eMUSEUM_PAGINATION_LIMIT

  const metaData = {
    page,
    totalPage,
    activeCollection,
    sort,
    totalCount,
    limit,
  }

  const pageData = {
    data: {
      hero: null,
      collection: null,
    },
    error: null,
  }

  // check safe activeCollection type
  const activeCollectionTypes = [
    'exhibitions',
    'events',
    'learning',
    'online',
    'workshop',
    'tours',
  ]
  const isSafeType = activeCollectionTypes.some((o) => o === activeCollection)

  if (!isSafeType) {
    pageData.error = 'Not valid active collection type.'
    return {
      props: { pageData, metaData },
    }
  }

  try {
    if (activeCollection === 'exhibitions') {
      // prepare exhibitions
      if (sort != 'past') {
        const currentExhibitions =
          await ExhibitionService.getExhibitionBySortName(
            locale,
            'current',
            `page=${metaData.page}`
          )
        const futureExhibitions =
          await ExhibitionService.getExhibitionBySortName(
            locale,
            'future',
            `page=${metaData.page}`
          )
        const exhibitions = [
          ...currentExhibitions.data,
          ...futureExhibitions.data,
        ]

        {
          /*
      const exhibitions = await ExhibitionService.getExhibitionBySortName(
        locale,
        sort,
        `page=${metaData.page}`
      )
        */
        }

        const atliKoskEvent = {
          id: 'atlikosk',
          title: 'Atlı Köşk',
          imgUrl:
            'https://drupal.sakipsabancimuzesi.org/sites/default/files/2024-12/atli-kosk.jpg',
          url: '/sayfa/atli-kosk-acildi',
          type: 'common:tabs.exhibitions',
        }

        exhibitions.push(atliKoskEvent)
        pageData.data.collection = exhibitions
        metaData.totalCount = exhibitions.length
        metaData.totalPage = Math.ceil(
          metaData.totalCount / metaData.limit
        ).toString()
      } else {
        const exhibitions = await ExhibitionService.getExhibitionBySortName(
          locale,
          sort,
          `page=${metaData.page}`
        )
        pageData.data.collection = exhibitions.data
        metaData.totalCount = exhibitions.totalCount
        metaData.totalPage = Math.ceil(
          metaData.totalCount / metaData.limit
        ).toString()
      }
    }

    if (activeCollection === 'events') {
      const params = new URLSearchParams();
      params.append('page', metaData.page.toString())
      if (query.eventType) {
        params.append('eventType', query.eventType)
      }
      if (sort != 'past') {
        const currentEvents = await EventService.getEventsBySortName(
          locale,
          'Event',
          'Current',
          params.toString()
        )

        const futureEvents = await EventService.getEventsBySortName(
          locale,
          'Event',
          'Future',
          params.toString()
        )
        const events = [...currentEvents.data, ...futureEvents.data]
        pageData.data.collection = events
        metaData.totalCount = events.length
        metaData.totalPage = Math.ceil(
          metaData.totalCount / metaData.limit
        ).toString()
      } else {
        const events = await EventService.getEventsBySortName(
          locale,
          'Event',
          'Past',
          params.toString()
        )

        pageData.data.collection = events.data
        metaData.totalCount = events.totalCount
        metaData.totalPage = Math.ceil(
          metaData.totalCount / metaData.limit
        ).toString()
      }
    }

    if (activeCollection === 'learning') {

      const params = new URLSearchParams();
      params.append('page', metaData.page.toString())
      if (query.eventType) {
        params.append('eventType', query.eventType)
      }
      if (sort != 'past') {
        const currentLearnings = await EventService.getEventsBySortName(
          locale,
          'Learning',
          'Current',
          params.toString()
        )
        const futureLearnings = await EventService.getEventsBySortName(
          locale,
          'Learning',
          'Future',
          params.toString()
        )
        // as per business request by event and learning, tours will be added to current learnings. For the past they won't be displayed
        const tours = []; // (await ToursService.getTourCategories(locale)) || []
        const learnings = [
          ...currentLearnings.data,
          ...futureLearnings.data.concat().reverse(),
          ...tours,
        ]

        pageData.data.collection = learnings
        metaData.totalCount = learnings.length
        metaData.totalPage = Math.ceil(
          metaData.totalCount / metaData.limit
        ).toString()
      } else {
        const events = await EventService.getEventsBySortName(
          locale,
          'Learning',
          'Past',
          params.toString()
        )
        pageData.data.collection = events.data
        metaData.totalCount = events.totalCount
        metaData.totalPage = Math.ceil(
          metaData.totalCount / metaData.limit
        ).toString()
      }
    }

    if (activeCollection === 'online') {
      metaData.sort = 'current'
      const onlineExhibitions =
        (await OnlineExhibitionsService.getOnlineExhibitions(locale)) || []

      pageData.data.collection = onlineExhibitions
      metaData.totalCount = onlineExhibitions.length
      metaData.totalPage = Math.ceil(
        metaData.totalCount / metaData.limit
      ).toString()
    }

    if (activeCollection === 'tours') {
      metaData.sort = 'current'
      const tours = (await ToursService.getTourCategories(locale)) || []

      pageData.data.collection = tours
      metaData.totalCount = tours.length
      metaData.totalPage = Math.ceil(
        metaData.totalCount / metaData.limit
      ).toString()
    }

    if (activeCollection === 'workshop') {
      if (sort != 'past') {
        const currentWorkshops = await EventService.getWorkshopsBySortName(
          locale,
          'Current',
          `page=${metaData.page}`
        )
        const futureWorkshops = await EventService.getWorkshopsBySortName(
          locale,
          'Future',
          `page=${metaData.page}`
        )
        const workshops = [...currentWorkshops.data, ...futureWorkshops.data]

        pageData.data.collection = workshops
        metaData.totalCount = workshops.length
        metaData.totalPage = Math.ceil(
          metaData.totalCount / metaData.limit
        ).toString()
      } else {
        const workshops = await EventService.getWorkshopsBySortName(
          locale,
          'Past',
          `page=${metaData.page}`
        )

        pageData.data.collection = workshops.data
        metaData.totalCount = workshops.totalCount
        metaData.totalPage = Math.ceil(
          metaData.totalCount / metaData.limit
        ).toString()
      }
    }

    // prepare hero
    const pageDataOnDrupal = await PageService.getDefaultLayoutPageData(
      locale,
      'exhibitionAndEvent'
    )

    if (
      pageDataOnDrupal &&
      pageDataOnDrupal?.data &&
      pageDataOnDrupal?.data?.hero
    ) {
      pageData.data.hero = pageDataOnDrupal?.data?.hero
    }
  } catch (err) {
    if (err instanceof Error) {
      pageData.error = { message: err.message }
    }
  }

  return {
    props: { pageData, metaData },
  }
}
